import { z, type ZodType } from 'zod';

import { jsonRequest } from './shared';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  signal?: AbortSignal;
}

async function requestJson<T>(path: string, { method = 'GET', body, signal }: RequestOptions = {}): Promise<T> {
  return jsonRequest<T>(path, {
    method,
    body,
    signal,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    schema: z.any() as ZodType<T>,
  });
}

export type TaxMode = 'exclusive' | 'inclusive';

export interface RestaurantPreferences {
  notifications: boolean;
  autoRefresh: boolean;
  soundAlerts: boolean;
  printerEnabled: boolean;
  backupEnabled: boolean;
  emailNotifications: boolean;
  smsAlerts: boolean;
  darkModeSchedule: boolean;
}

export interface RestaurantSettings {
  id: string;
  name: string;
  timezone: string;
  taxMode: TaxMode;
  contactEmail: string | null;
  contactPhone: string | null;
  address: string | null;
  currency: string;
  taxRate: number;
  serviceCharge: number;
  cardSurcharge: number;
  preferences: RestaurantPreferences;
  financialProfiles?: FinancialProfilesSnapshot;
}

export interface FetchRestaurantResponse {
  restaurant: RestaurantSettings;
}

export interface UpdateRestaurantPayload {
  name: string;
  timezone: string;
  taxMode: TaxMode;
  contactEmail?: string | null;
  contactPhone?: string | null;
  address?: string | null;
  currency?: string;
  taxRate?: number;
  serviceCharge?: number;
  cardSurcharge?: number;
  notifications?: boolean;
  autoRefresh?: boolean;
  soundAlerts?: boolean;
  printerEnabled?: boolean;
  backupEnabled?: boolean;
  emailNotifications?: boolean;
  smsAlerts?: boolean;
  darkModeSchedule?: boolean;
}

export interface UpdateRestaurantResponse {
  restaurant: RestaurantSettings;
}

export type TaxProfileTarget = 'global' | 'location' | 'category' | 'menu_item';

export interface TaxProfile {
  id: string;
  name: string;
  target: TaxProfileTarget;
  targetId: string | null;
  rateBps: number;
  serviceChargeBps: number;
  stackWithDefaults: boolean;
  appliesAt: string | null;
  expiresAt: string | null;
  createdAt: number;
  updatedAt: number;
}

export type TipProfileTarget = 'global' | 'location' | 'service_type';
export type TipProfileType = 'percentage' | 'fixed';

export interface TipProfile {
  id: string;
  name: string;
  target: TipProfileTarget;
  targetId: string | null;
  type: TipProfileType;
  value: number;
  autoApply: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface DiscountReasonCode {
  code: string;
  label: string;
  requiresNote: boolean;
}

export type DiscountType = 'percentage' | 'amount';

export interface DiscountPolicy {
  id: string;
  name: string;
  code: string;
  type: DiscountType;
  value: number;
  maxStack?: number | null;
  stackGroup?: string | null;
  allowStacking: boolean;
  approvalRequired: boolean;
  reasonCodes: DiscountReasonCode[];
  createdAt: number;
  updatedAt: number;
}

export interface DiscountWorkflow {
  id: string;
  name: string;
  thresholdCents: number;
  approverRoles: string[];
  escalationEmails: string[];
  requireReasonCode: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface FinancialProfilesSnapshot {
  taxProfiles: TaxProfile[];
  tipProfiles: TipProfile[];
  discountPolicies: DiscountPolicy[];
  discountWorkflows: DiscountWorkflow[];
}

export type FinancialProfilesPayload = FinancialProfilesSnapshot;

export type FinancialProfilesResponse = FinancialProfilesSnapshot;

export const fetchRestaurantSettings = (signal?: AbortSignal) =>
  requestJson<FetchRestaurantResponse>('/settings/restaurant', { signal });

export const updateRestaurantSettings = (payload: UpdateRestaurantPayload) =>
  requestJson<UpdateRestaurantResponse>('/settings/restaurant', { method: 'PUT', body: payload });

export const fetchFinancialProfiles = (signal?: AbortSignal) =>
  requestJson<FinancialProfilesResponse>('/settings/financial-profiles', { signal });

export const updateFinancialProfiles = (payload: FinancialProfilesPayload) =>
  requestJson<FinancialProfilesResponse>('/settings/financial-profiles', { method: 'PUT', body: payload });

export type StaffLifecycleStatus = 'active' | 'on_break' | 'off_duty' | 'invited' | 'inactive';

export interface StaffMember {
  id: string;
  email: string;
  role: string;
  name: string | null;
  displayName: string | null;
  phone: string | null;
  department: string | null;
  defaultShift: string | null;
  invitedAt: string | null;
  joinedAt: string | null;
  status: StaffLifecycleStatus;
  currentStatus: StaffLifecycleStatus;
  activeShiftId: string | null;
  activeShiftTimezone: string | null;
  lastClockEventAt: string | null;
  pinRotatedAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  deletedBy: string | null;
  restoredAt: string | null;
  restoredBy: string | null;
}

export interface FetchStaffResponse {
  staff: StaffMember[];
}

export const fetchSettingsStaff = (signal?: AbortSignal) =>
  requestJson<FetchStaffResponse>('/settings/staff', { signal });

export interface InviteStaffPayload {
  email: string;
  role: string;
  name?: string;
  phone?: string;
  department?: string;
  defaultShift?: string;
  pin?: string;
  approverPin: string;
}

export interface InviteStaffResponse {
  member: StaffMember;
  temporaryPin?: string;
}

export const inviteSettingsStaff = (payload: InviteStaffPayload) =>
  requestJson<InviteStaffResponse>('/settings/staff/invite', { method: 'POST', body: payload });

export interface UpdateStaffPayload {
  role?: string;
  name?: string;
  phone?: string;
  department?: string;
  defaultShift?: string;
}

export const updateSettingsStaff = (id: string, payload: UpdateStaffPayload) =>
  requestJson<{ member: StaffMember }>(`/settings/staff/${id}`, { method: 'PATCH', body: payload });

export interface SetStaffPinPayload {
  pin: string;
  approverPin: string;
}

export const setSettingsStaffPin = (id: string, payload: SetStaffPinPayload) =>
  requestJson<{ member: StaffMember }>(`/settings/staff/${id}/pin`, { method: 'POST', body: payload });

export interface StaffClockEventPayload {
  action: 'clock-in' | 'clock-out' | 'break-start' | 'break-end';
  pin: string;
}

export const recordSettingsStaffClockEvent = (id: string, payload: StaffClockEventPayload) =>
  requestJson<{ member: StaffMember }>(`/settings/staff/${id}/clock-events`, { method: 'POST', body: payload });

export const deactivateSettingsStaff = (id: string) =>
  requestJson<{ member: StaffMember }>(`/settings/staff/${id}/deactivate`, { method: 'POST' });

export const reactivateSettingsStaff = (id: string) =>
  requestJson<{ member: StaffMember }>(`/settings/staff/${id}/reactivate`, { method: 'POST' });

export interface PrinterRecord {
  id: string;
  name: string;
  type: string;
  connection: Record<string, unknown> | null;
  locationId: string | null;
  location: string;
  status: 'online' | 'offline' | 'unknown';
  capabilities: string[];
  queueDepth: number;
  heartbeatIntervalMs: number;
  offlineGraceMs: number;
  labelsEnabled: boolean;
  receiptsEnabled: boolean;
  chitsEnabled: boolean;
  firmware: string | null;
  ipAddress: string | null;
  model: string | null;
  serialNumber: string | null;
  errorState: string | null;
  lastSeenAt: string | null;
  lastErrorAt: string | null;
  lastTestedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export const fetchPrinters = (signal?: AbortSignal) =>
  requestJson<{ printers: PrinterRecord[] }>('/settings/printers', { signal });

export const testPrinter = (id: string) =>
  requestJson<{ ok: boolean; preview: string; template?: string }>(`/settings/printers/${id}/test`, {
    method: 'POST',
  });

export interface PrinterJobRecord {
  id: string;
  printerId: string;
  tenantId: string;
  locationId: string | null;
  jobType: string;
  payload: Record<string, unknown>;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'fallback';
  attempts: number;
  maxAttempts: number;
  lastAttemptAt: string | null;
  nextRetryAt: string | null;
  slaDeadline: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  errorMessage: string | null;
  printer: {
    id: string;
    name: string;
    status: PrinterRecord['status'];
    locationId: string | null;
    capabilities: string[];
    errorState: string | null;
    queueDepth: number;
    lastSeenAt: string | null;
  };
}

export interface PrinterJobEventRecord {
  id: string;
  jobId: string;
  printerId: string;
  eventType:
    | 'queued'
    | 'attempt_started'
    | 'attempt_succeeded'
    | 'attempt_failed'
    | 'completed'
    | 'failed'
    | 'fallback_routed'
    | 'canceled'
    | 'timeout';
  payload: Record<string, unknown>;
  errorMessage: string | null;
  createdAt: string;
}

export const enqueuePrinterJob = (
  printerId: string,
  payload: { jobType: string; payload?: Record<string, unknown>; slaDeadline?: number; maxAttempts?: number },
) =>
  requestJson<{ ok: boolean; job: PrinterJobRecord }>(`/printers/${printerId}/print`, {
    method: 'POST',
    body: payload,
  });

export const fetchPrinterJobs = (params?: { status?: string; printerId?: string; limit?: number }, signal?: AbortSignal) => {
  const searchParams = new URLSearchParams();
  if (params?.status) {
    searchParams.set('status', params.status);
  }
  if (params?.printerId) {
    searchParams.set('printerId', params.printerId);
  }
  if (typeof params?.limit === 'number' && Number.isFinite(params.limit)) {
    searchParams.set('limit', String(params.limit));
  }
  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : '';
  return requestJson<{ ok: boolean; jobs: PrinterJobRecord[] }>(`/printers/jobs${suffix}`, { signal });
};

export const fetchPrinterJob = (jobId: string, signal?: AbortSignal) =>
  requestJson<{ ok: boolean; job: PrinterJobRecord; events: PrinterJobEventRecord[] }>(`/printers/jobs/${jobId}`, {
    signal,
  });

export type PrinterOutputRole = 'front_desk_receipt' | 'kitchen_ticket';

export interface PrinterRoleRecord {
  id: string;
  role: PrinterOutputRole;
  copies: number;
  copyLabels: string[];
  createdAt: string;
  updatedAt: string;
  location: {
    id: string;
    name: string | null;
  };
  printer: {
    id: string;
    name: string;
    status: PrinterRecord['status'];
  };
}

export const fetchPrinterRoles = (params?: { locationId?: string }, signal?: AbortSignal) => {
  const searchParams = new URLSearchParams();
  if (params?.locationId) {
    searchParams.set('locationId', params.locationId);
  }
  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : '';
  return requestJson<{ roles: PrinterRoleRecord[] }>(`/settings/printer-roles${suffix}`, { signal });
};

export interface UpsertPrinterRolePayload {
  id?: string;
  role: PrinterOutputRole;
  locationId: string;
  printerId: string;
  copies?: number;
  copyLabels?: string[];
}

export const upsertPrinterRole = (payload: UpsertPrinterRolePayload) =>
  requestJson<{ role: PrinterRoleRecord }>('/settings/printer-roles', {
    method: 'POST',
    body: payload,
  });

export const deletePrinterRole = (id: string) =>
  requestJson<void>(`/settings/printer-roles/${encodeURIComponent(id)}`, { method: 'DELETE' });

export interface BillingPlan {
  id: string;
  name: string;
  pricePerMonthCents: number;
  billingCycle: 'monthly' | 'annual';
  renewalDate: string;
  status: 'active' | 'trial' | 'past_due';
  seats: number;
}

export interface InvoiceRecord {
  id: string;
  number: string;
  amountCents: number;
  issuedAt: string;
  dueAt: string;
  status: 'paid' | 'due' | 'pending';
}

export const fetchBilling = (signal?: AbortSignal) =>
  requestJson<{ plan: BillingPlan; invoices: InvoiceRecord[] }>('/settings/billing', { signal });

export interface ApiKeySummary {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
  status: 'active' | 'revoked';
  lastFour: string;
}

export interface ApiKeyRecord extends ApiKeySummary {
  secret?: string;
}

export const fetchApiKeys = (signal?: AbortSignal) =>
  requestJson<{ apiKeys: ApiKeySummary[] }>('/settings/api-keys', { signal });

export const createApiKey = (label: string) =>
  requestJson<{ apiKey: ApiKeyRecord }>('/settings/api-keys', { method: 'POST', body: { label } });

export const rotateApiKey = (id: string) =>
  requestJson<{ apiKey: ApiKeyRecord }>(`/settings/api-keys/${id}/rotate`, { method: 'POST' });

export const revokeApiKey = (id: string) => requestJson<void>(`/settings/api-keys/${id}`, { method: 'DELETE' });

export interface TenantLocationRecord {
  id: string;
  name: string;
  timezone: string;
  currency: string;
  defaultTaxRateBps?: number | null;
  defaultServiceChargeBps?: number | null;
  defaultCardSurchargeBps?: number | null;
  qrOrderingStatus: 'active' | 'paused' | 'stopped';
  qrOrderingMessage: string | null;
  qrOrderingUpdatedAt: number | null;
  qrOrderingUpdatedBy: string | null;
}

export const fetchTenantLocations = (signal?: AbortSignal) =>
  requestJson<{ locations: TenantLocationRecord[] }>('/settings/locations', { signal });

export const createTenantLocation = (payload: {
  name: string;
  timezone?: string;
  currency?: string;
  defaultTaxRateBps?: number;
  defaultServiceChargeBps?: number;
  defaultCardSurchargeBps?: number;
}) =>
  requestJson<{ location: TenantLocationRecord }>('/settings/locations', {
    method: 'POST',
    body: payload,
  });

export interface UpdateTenantLocationPayload {
  name?: string;
  timezone?: string;
  currency?: string;
  defaultTaxRateBps?: number;
  defaultServiceChargeBps?: number;
  defaultCardSurchargeBps?: number;
  qrOrderingStatus?: 'active' | 'paused' | 'stopped';
  qrOrderingMessage?: string | null;
}

export const updateTenantLocation = (locationId: string, payload: UpdateTenantLocationPayload) =>
  requestJson<{ location: TenantLocationRecord }>(`/settings/locations/${encodeURIComponent(locationId)}`, {
    method: 'PATCH',
    body: payload,
  });

export interface LocationHourBreakPeriod {
  start: string;
  end: string;
}

export interface LocationHourRecord {
  id: string;
  locationId: string;
  dayOfWeek: number;
  openTime: string | null;
  closeTime: string | null;
  isClosed: boolean;
  breakPeriods: LocationHourBreakPeriod[];
  notes: string | null;
  effectiveStart: string | null;
  effectiveEnd: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface LocationHourPayload {
  id?: string;
  dayOfWeek: number;
  openTime?: string | null;
  closeTime?: string | null;
  isClosed?: boolean;
  breakPeriods?: LocationHourBreakPeriod[];
  notes?: string | null;
}

export const fetchLocationHours = (locationId: string, signal?: AbortSignal) =>
  requestJson<{ hours: LocationHourRecord[] }>(
    `/settings/locations/${encodeURIComponent(locationId)}/hours`,
    { signal },
  );

export const updateLocationHours = (locationId: string, payload: { hours: LocationHourPayload[] }) =>
  requestJson<{ hours: LocationHourRecord[] }>(
    `/settings/locations/${encodeURIComponent(locationId)}/hours`,
    {
      method: 'PUT',
      body: payload,
    },
  );

export interface LocationHourOverride {
  id: string;
  tenantId: string;
  locationId: string;
  overrideDate: string;
  openTime: string | null;
  closeTime: string | null;
  isClosed: boolean;
  reason: string | null;
  metadata: Record<string, unknown>;
  createdAt: number;
  updatedAt: number;
}

export interface LocationOverridePayload {
  overrideDate: string;
  openTime?: string | null;
  closeTime?: string | null;
  isClosed?: boolean;
  reason?: string | null;
}

export const fetchLocationOverrides = (
  locationId: string,
  params?: { start?: string; end?: string },
  signal?: AbortSignal,
) => {
  const query = new URLSearchParams();
  if (params?.start) query.set('start', params.start);
  if (params?.end) query.set('end', params.end);
  const suffix = query.toString();
  return requestJson<{ overrides: LocationHourOverride[] }>(
    `/settings/locations/${encodeURIComponent(locationId)}/hour-overrides${suffix ? `?${suffix}` : ''}`,
    { signal },
  );
};

export const createLocationOverride = (locationId: string, payload: LocationOverridePayload) =>
  requestJson<{ override: LocationHourOverride }>(
    `/settings/locations/${encodeURIComponent(locationId)}/hour-overrides`,
    {
      method: 'POST',
      body: payload,
    },
  );

export const updateLocationOverride = (
  locationId: string,
  overrideId: string,
  payload: Partial<LocationOverridePayload>,
) =>
  requestJson<{ override: LocationHourOverride }>(
    `/settings/locations/${encodeURIComponent(locationId)}/hour-overrides/${encodeURIComponent(overrideId)}`,
    {
      method: 'PATCH',
      body: payload,
    },
  );

export const deleteLocationOverride = (locationId: string, overrideId: string) =>
  requestJson<void>(
    `/settings/locations/${encodeURIComponent(locationId)}/hour-overrides/${encodeURIComponent(overrideId)}`,
    { method: 'DELETE' },
  );

export interface LocationFinancialConfig {
  locationId: string;
  tenantId: string;
  taxProfileId: string | null;
  tipProfileId: string | null;
  serviceChargeBps: number | null;
  cardSurchargeBps: number | null;
  autoApplyTip: boolean;
  metadata: Record<string, unknown>;
  updatedAt: number;
  updatedBy: string | null;
}

export const fetchLocationFinancialConfig = (locationId: string, signal?: AbortSignal) =>
  requestJson<{ config: LocationFinancialConfig | null }>(
    `/settings/locations/${encodeURIComponent(locationId)}/financial-config`,
    { signal },
  );

export const updateLocationFinancialConfig = (
  locationId: string,
  payload: Partial<{
    taxProfileId: string | null;
    tipProfileId: string | null;
    serviceChargeBps: number | null;
    cardSurchargeBps: number | null;
    autoApplyTip: boolean;
  }>,
) =>
  requestJson<{ config: LocationFinancialConfig | null }>(
    `/settings/locations/${encodeURIComponent(locationId)}/financial-config`,
    {
      method: 'PUT',
      body: payload,
    },
  );

export interface LocationDeviceRecord {
  id: string;
  tenantId: string;
  locationId: string | null;
  name: string;
  deviceType: 'pos' | 'kds' | 'printer' | 'tablet' | 'other';
  status: 'active' | 'inactive' | 'maintenance' | 'retired';
  platform: string | null;
  osVersion: string | null;
  appVersion: string | null;
  serialNumber: string | null;
  lastSeenAt: number | null;
  pairedAt: number | null;
  assignedTo: string | null;
  tags: string[];
  metadata: Record<string, unknown>;
  createdAt: number;
  updatedAt: number;
}

export const fetchLocationDevices = (locationId: string, signal?: AbortSignal) =>
  requestJson<{ devices: LocationDeviceRecord[] }>(
    `/settings/locations/${encodeURIComponent(locationId)}/devices`,
    { signal },
  );

export const updateLocationDevice = (
  locationId: string,
  deviceId: string,
  payload: Partial<{ status: LocationDeviceRecord['status']; name: string; tags: string[] }>,
) =>
  requestJson<{ device: LocationDeviceRecord }>(
    `/settings/locations/${encodeURIComponent(locationId)}/devices/${encodeURIComponent(deviceId)}`,
    {
      method: 'PATCH',
      body: payload,
    },
  );
